// src/components/dashboard/EnergyDemandReductionCard.js
import React, { useMemo } from "react";
import { Line } from "react-chartjs-2";
import { useEnergyReductionAligned } from "../../hooks/useEnergyReductionAligned";

export default function EnergyDemandReductionCard({
  scenario,
  from = 2025,
  to = 2050,
  compareYear = 2025,

  comparisonScenarioKey = null,
  selectedComparisonValue = "",
  onChangeComparison = null,
}) {
  const A = useEnergyReductionAligned({ scenario, from, to, baselineYearPref: compareYear });
  const B = useEnergyReductionAligned({ scenario: comparisonScenarioKey || undefined, from, to, baselineYearPref: compareYear });

  const unionYears = useMemo(() => {
    const set = new Set(A.years ?? []);
    if (comparisonScenarioKey && B.years) for (const y of B.years) set.add(y);
    return Array.from(set).sort((a, b) => a - b);
  }, [A.years, B.years, comparisonScenarioKey]);

  const labels = useMemo(() => unionYears.map(String), [unionYears]);

  const fmtTWh = (n) => (Number.isFinite(n) ? n.toLocaleString("en-GB") : "—");

  const datasets = useMemo(() => {
    const ds = [];

    if (A.hasData) {
      ds.push({
        label: `Your Scenario`,
        data: unionYears.map((y) => {
          const v = A.totalByYearTWh.get(y);
          return typeof v === "number" ? Number(v.toFixed(2)) : null;
        }),
        borderColor: "#2563EB",
        backgroundColor: "rgba(37, 99, 235, 0.12)",
        tension: 0.3,
        fill: false,
        pointRadius: 0,
        spanGaps: true,
      });
    }

    if (comparisonScenarioKey && B.hasData && !B.loading && !B.error) {
      ds.push({
        label: `Comparison`,
        data: unionYears.map((y) => {
          const v = B.totalByYearTWh.get(y);
          return typeof v === "number" ? Number(v.toFixed(2)) : null;
        }),
        borderColor: "#8b5cf6",
        backgroundColor: "rgba(139, 92, 246, 0.10)",
        tension: 0.3,
        fill: false,
        pointRadius: 0,
        borderDash: [5, 5],
        spanGaps: true,
      });
    }

    return ds;
  }, [A.hasData, A.totalByYearTWh, B.hasData, B.totalByYearTWh, comparisonScenarioKey, unionYears]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: { position: "top" },
      title: { display: true, text: "Energy Demand Reduction vs 2025" },
      tooltip: {
        callbacks: {
          label: (ctx) => `${ctx.dataset.label}: ${fmtTWh(Number(ctx.parsed.y))} TWh`,
        },
      },
    },
    scales: {
      x: { title: { display: true, text: "Year" } },
      y: {
        min: 0,
        max: 1600,
        title: { display: true, text: "TWh" },
        beginAtZero: false,            // let the decline show naturally
        ticks: {
          callback: (v) => v.toLocaleString("en-GB"),
        },
      },
    },
  };

  const loading = A.loading || (comparisonScenarioKey && B.loading);
  const error = A.error || (comparisonScenarioKey && B.error);

 const latestYear = A.years?.[A.years.length - 1] ?? null;
const baselineYearUsed = A.baselineYearUsed ?? compareYear; // hook already prefers compareYear
const latestTWh = latestYear != null ? A.totalByYearTWh.get(latestYear) : null;
const baseTWh   = baselineYearUsed != null ? A.totalByYearTWh.get(baselineYearUsed) : null;

const pctOfBaseline =
  Number.isFinite(baseTWh) && Number.isFinite(latestTWh) && baseTWh !== 0
    ? (latestTWh / baseTWh) * 100
    : null;

// now: percent *reduction* from 2025 to 2050
const reductionPct =
  pctOfBaseline != null ? 100 - pctOfBaseline : null;

// (optional: guard for increases)
const noun = reductionPct != null && reductionPct < 0 ? "increase" : "reduction";
const headline =
  reductionPct != null ? `${Math.abs(reductionPct).toFixed(1)}%` : "—";
  return (
    <div className="bg-white p-4 rounded-lg flex flex-col items-center">
      <h3 className="text-sm font-medium text-gray-700">Energy Demand Reduction</h3>

      <div className="mt-2 w-full flex flex-col items-center">
        <div className="flex items-end">
          <div className="text-3xl font-bold text-blue-600">
            {loading ? "…" : headline} {noun} by 2050
          </div>

        </div>

        <div className="w-full h-96 mt-2">
          {error ? (
            <div className="w-full h-full grid place-items-center text-red-600">
              Error: {error.message}
            </div>
          ) : (
            <Line data={{ labels, datasets }} options={options} />
          )}
        </div>
      </div>
    </div>
  );
}
